import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Clock, ArrowLeft, Tag, ChevronRight, BookOpen, Share2, User } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { mockBlogs, BLOG_TAGS } from '@/data/news-blogs-data'

function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Simple content renderer supporting **bold**, numbered lists, bullets, emoji headers
function renderContent(content: string) {
    const lines = content.split('\n')
    return lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-3" />

        if (line.startsWith('**') && line.endsWith('**')) {
            const text = line.slice(2, -2)
            return <h3 key={i} className="text-lg font-bold text-theme-primary mt-6 mb-2">{text}</h3>
        }

        if (/^[🔴🟡🟢🔵]/.test(line)) {
            const clean = line.replace(/\*\*(.*?)\*\*/g, (_m, p1) => p1)
            return <p key={i} className="font-semibold text-theme-primary mt-4 mb-1">{clean}</p>
        }

        if (/^\d+\./.test(line)) {
            const withBold = line.split(/\*\*(.*?)\*\*/g)
            return (
                <li key={i} className="text-theme-secondary ml-4 mb-1 list-decimal">
                    {withBold.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-theme-primary">{part}</strong> : part)}
                </li>
            )
        }

        if (line.startsWith('- ')) {
            const text = line.slice(2)
            const withBold = text.split(/\*\*(.*?)\*\*/g)
            return (
                <li key={i} className="text-theme-secondary ml-4 mb-1 list-disc">
                    {withBold.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-theme-primary">{part}</strong> : part)}
                </li>
            )
        }

        const withBold = line.split(/\*\*(.*?)\*\*/g)
        return (
            <p key={i} className="text-theme-secondary leading-relaxed mb-2">
                {withBold.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-theme-primary font-semibold">{part}</strong> : part)}
            </p>
        )
    })
}

export default function BlogDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const post = mockBlogs.find(b => b.id === id)

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <GlassmorphicCard className="p-12 text-center max-w-md">
                    <BookOpen className="h-12 w-12 text-theme-muted mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-theme-primary mb-2">Post Not Found</h2>
                    <p className="text-theme-secondary mb-6">This blog post doesn't exist or may have been removed.</p>
                    <Link to="/blogs" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-blue text-white font-semibold rounded-xl">
                        <ArrowLeft className="h-4 w-4" /> Back to Blog
                    </Link>
                </GlassmorphicCard>
            </div>
        )
    }

    const related = mockBlogs.filter(b => b.id !== post.id && b.category === post.category).slice(0, 3)
    const popularPosts = [...mockBlogs]
        .filter(b => b.id !== post.id)
        .sort((a, b) => parseFloat(b.views) - parseFloat(a.views))
        .slice(0, 5)

    return (
        <div className="min-h-screen relative">
            {/* Hero image banner */}
            <div className="relative h-72 md:h-96 overflow-hidden">
                <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12">
                    <div className="container mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="max-w-3xl"
                        >
                            <span className="inline-block px-3 py-1 bg-primary-blue text-white text-xs font-semibold rounded-full mb-3">
                                {post.category}
                            </span>
                            <h1 className="text-2xl md:text-4xl font-display font-bold text-white leading-tight mb-3">{post.title}</h1>
                            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                                <span className="flex items-center gap-1.5"><User className="h-4 w-4" />{post.author}</span>
                                <span className="text-white/50">·</span>
                                <span className="text-white/60 text-xs">{post.authorRole}</span>
                                <span className="text-white/50">·</span>
                                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(post.date)}</span>
                                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{post.readTime}</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Breadcrumb */}
            <div className="container mx-auto px-4 pt-6">
                <div className="flex items-center gap-2 text-sm text-theme-muted mb-6">
                    <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
                    <ChevronRight className="h-3.5 w-3.5" />
                    <Link to="/blogs" className="hover:text-primary-blue transition-colors">Blog</Link>
                    <ChevronRight className="h-3.5 w-3.5" />
                    <span className="text-theme-secondary truncate max-w-xs">{post.title}</span>
                </div>
            </div>

            <section className="pb-16">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Blog content */}
                        <div className="lg:col-span-2">
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <GlassmorphicCard className="p-6 md:p-10">
                                    {/* Author card */}
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-primary-blue/5 border border-primary-blue/10 mb-8">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                                            {post.author.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-theme-primary">{post.author}</p>
                                            <p className="text-sm text-theme-muted">{post.authorRole}</p>
                                        </div>
                                        <div className="ml-auto text-right hidden sm:block">
                                            <p className="text-xs text-theme-muted">{formatDate(post.date)}</p>
                                            <p className="text-xs text-primary-blue font-medium">{post.readTime}</p>
                                        </div>
                                    </div>

                                    {/* Excerpt lead */}
                                    <p className="text-base md:text-lg text-theme-secondary font-medium leading-relaxed mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
                                        {post.excerpt}
                                    </p>

                                    {/* Full content */}
                                    <div className="prose-like">
                                        {renderContent(post.content)}
                                    </div>

                                    {/* Tags + share */}
                                    <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex flex-wrap gap-2">
                                            {post.tags.map(t => (
                                                <span key={t} className="px-3 py-1 bg-primary-blue/10 text-primary-blue text-xs font-medium rounded-full">{t}</span>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => navigator.share ? navigator.share({ title: post.title, url: window.location.href }) : undefined}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                        >
                                            <Share2 className="h-4 w-4" /> Share
                                        </button>
                                    </div>
                                </GlassmorphicCard>

                                <button
                                    onClick={() => navigate(-1)}
                                    className="mt-6 inline-flex items-center gap-2 text-sm text-theme-secondary hover:text-primary-blue transition-colors"
                                >
                                    <ArrowLeft className="h-4 w-4" /> Back to Blog
                                </button>
                            </motion.div>

                            {/* Related posts */}
                            {related.length > 0 && (
                                <div className="mt-10">
                                    <h2 className="text-xl font-bold text-theme-primary mb-4">More in {post.category}</h2>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {related.map(r => (
                                            <Link key={r.id} to={`/blogs/${r.id}`}>
                                                <GlassmorphicCard className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-300 h-full">
                                                    <div className="h-32 overflow-hidden">
                                                        <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                    </div>
                                                    <div className="p-4">
                                                        <p className="font-semibold text-theme-primary text-sm leading-snug line-clamp-2 group-hover:text-primary-blue transition-colors">{r.title}</p>
                                                        <div className="flex items-center gap-2 text-xs text-theme-muted mt-2">
                                                            <span>{r.author}</span>
                                                            <span>·</span>
                                                            <span>{r.readTime}</span>
                                                        </div>
                                                    </div>
                                                </GlassmorphicCard>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Popular posts */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <BookOpen className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Popular Posts</h3>
                                </div>
                                <div className="space-y-4">
                                    {popularPosts.map(p => (
                                        <Link key={p.id} to={`/blogs/${p.id}`} className="flex gap-3 group cursor-pointer">
                                            <div className="w-16 h-12 rounded-lg overflow-hidden shrink-0">
                                                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-theme-primary group-hover:text-primary-blue transition-colors line-clamp-2 leading-snug">{p.title}</p>
                                                <p className="text-xs text-theme-muted mt-0.5">{p.views} views</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* Tags */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Tag className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Popular Tags</h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {BLOG_TAGS.map(tag => (
                                        <Link
                                            key={tag}
                                            to="/blogs"
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${post.tags.includes(tag)
                                                ? 'bg-primary-blue text-white'
                                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-primary-blue/10 hover:text-primary-blue'
                                                }`}
                                        >
                                            {tag}
                                        </Link>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* CTA */}
                            <GlassmorphicCard className="p-5 bg-gradient-to-br from-primary-blue/10 to-blue-600/10 border-primary-blue/20">
                                <h3 className="font-bold text-theme-primary mb-2">Want to Contribute?</h3>
                                <p className="text-sm text-theme-secondary mb-4">Share your NEET journey with thousands of aspirants.</p>
                                <Link to="/contact">
                                    <button className="w-full py-2.5 bg-primary-blue hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-colors">
                                        Write for Us
                                    </button>
                                </Link>
                            </GlassmorphicCard>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}
